// vocabProgressStore.ts — vocab-cheatsheet progress and social state.
// Learning progress remains version-keyed so a bundle swap cannot mis-map
// content. Stars/bookmark are lemma-keyed social state and live in an account-
// scoped local record while the authenticated server profile is the durable source.
import * as userKgClient from '../../../api/userKgClient';
import { isAuthLikeToken } from '../../../../../lib/authMode';
import { resolveAuthIdentity } from '../../../../../lib/authIdentity';
import { VOCAB_CONTENT_VERSION, VOCAB_INDEX, deckIdOf, type VocabTier } from './vocabData';

export type WordState = 'unlearned' | 'learning' | 'learned' | 'needs-review' | 'skipped';
export type MasteryState = 'unknown' | 'learning' | 'familiar' | 'mastered';
export type DrillProgressState = 'familiar' | 'learning';

export type BrowseView = 'all' | 'starred';

export interface BrowseBookmark {
  view: BrowseView;
  page: number;
  wordId: string;
  savedAt?: string;
}

export interface ConfirmedSessionStats {
  unknown: number;
  learning: number;
  familiar: number;
  mastered: number;
}

export type ManualWordState = Exclude<WordState, 'unlearned'>;
export type WordSyncStatus = 'not-measured' | 'measured' | 'failed';
export type VocabProgressEvent =
  | { type: 'changed' }
  | { type: 'word-state-intent' | 'word-state-confirmed' | 'word-state-failed'; lemma: string; state: WordState };

export interface VocabProgress {
  contentVersion: string;
  perWord: Record<string, WordState>;
  cursor: { deckId: number; nextUnlearnedRank: number };
  resume: { lastWordId: string | null };
  drillMirror: Record<string, MasteryState>;
  /** Aggregate mastery snapshot returned by the server when a card session
   * resolves. Never updated from an optimistic client judgment. */
  confirmedSessionStats: ConfirmedSessionStats | null;
  serverMastered: Record<string, true>;
  serverDrillProgress: Record<string, DrillProgressState>;
  wordStateSyncStatus: WordSyncStatus;
  wordStateSyncError: string | null;
  /** Per-word "keep this in my personal revisit list" curation — never
   * touches perWord/drillMirror/cursor/tier%/deck% (PM §3). Missing/undefined
   * on read (pre-star-feature data) normalizes to `{}`. */
  starred: Record<string, true>;
  /** One saved browse position (list row or open detail word), independent
   * of the mastery-driven learning cursor (PM §4). Missing/undefined on read
   * normalizes to `null`. */
  browseBookmark: BrowseBookmark | null;
  updatedAt: string;
}

/** Percent of a tier's words marked learned — shared by the progress spine
 * and the sticky filter bar's tier chips so both surfaces show the same
 * number. Lives here (not a component file) so `react-refresh/only-export-
 * components` stays happy in VocabSpine.tsx. */
export function tierPercent(tier: VocabTier, progress: VocabProgress): number {
  const words = VOCAB_INDEX.words.filter((w) => w.tier === tier.tier);
  if (words.length === 0) return 0;
  const learned = words.filter((w) => effectiveWordState(progress, w.lemma) === 'learned').length;
  return Math.round((learned / words.length) * 100);
}

function storageKey(contentVersion: string): string {
  return `pp.vocab.progress.v0.${contentVersion}`;
}

const LEGACY_SOCIAL_STORAGE_KEY = 'pp.vocab.social.v0';
const LEGACY_SCOPED_SOCIAL_STORAGE_KEY_PREFIX = `${LEGACY_SOCIAL_STORAGE_KEY}:`;
const SOCIAL_STORAGE_KEY_PREFIX = 'pp.vocab.social.v1:';
export const WORD_STATE_STORAGE_KEY = 'pp.vocab.word-states.v0';

interface SocialState {
  starred: Record<string, true>;
  bookmark: BrowseBookmark | null;
  dirtyStars: Record<string, boolean>;
  bookmarkDirty: boolean;
}

interface WordSyncState {
  manualStates: Record<string, ManualWordState>;
  dirtyWordStates: Record<string, ManualWordState | null>;
  serverMastered: Record<string, true>;
  serverDrillProgress: Record<string, DrillProgressState>;
  syncStatus: WordSyncStatus;
  lastError: string | null;
  /** Lemmas rejected 404 (not in the active collection), keyed to the server
   * content_version current at rejection time. Quarantined intents are kept
   * locally but never replayed until GET reports a DIFFERENT content_version
   * or the user explicitly re-marks the word (design §7 "dropped lemma"). */
  quarantined: Record<string, string>;
  /** Last content_version reported by a successful word-state GET/PUT. */
  serverContentVersion: string | null;
  /** Versioned local progress records already considered for anonymous-to-
   * authenticated import. This is explicit because a non-empty stable map is
   * not evidence that the current content version was imported. */
  importedVersions: string[];
}

function emptyWordSync(): WordSyncState {
  return {
    manualStates: {},
    dirtyWordStates: {},
    serverMastered: {},
    serverDrillProgress: {},
    syncStatus: 'not-measured',
    lastError: null,
    quarantined: {},
    serverContentVersion: null,
    importedVersions: [],
  };
}

let memoryWordSync: WordSyncState | null = null;

function normalizeWordSync(raw: any): WordSyncState {
  const state = emptyWordSync();
  if (!raw || typeof raw !== 'object') return state;
  for (const [lemma, value] of Object.entries(raw.manualStates ?? {})) {
    if (typeof lemma === 'string' && ['learning', 'learned', 'needs-review', 'skipped'].includes(String(value))) {
      state.manualStates[lemma] = value as ManualWordState;
    }
  }
  for (const [lemma, value] of Object.entries(raw.dirtyWordStates ?? {})) {
    if (typeof lemma !== 'string') continue;
    if (value === null) state.dirtyWordStates[lemma] = null;
    else if (['learning', 'learned', 'needs-review', 'skipped'].includes(String(value))) {
      state.dirtyWordStates[lemma] = value as ManualWordState;
    }
  }
  for (const [lemma, value] of Object.entries(raw.serverMastered ?? {})) {
    if (value === true && lemma) state.serverMastered[lemma] = true;
  }
  for (const [lemma, value] of Object.entries(raw.serverDrillProgress ?? {})) {
    if ((value === 'familiar' || value === 'learning') && lemma) state.serverDrillProgress[lemma] = value;
  }
  state.syncStatus = raw.syncStatus === 'measured' || raw.syncStatus === 'failed' ? raw.syncStatus : 'not-measured';
  state.lastError = typeof raw.lastError === 'string' ? raw.lastError : null;
  for (const [lemma, version] of Object.entries(raw.quarantined ?? {})) {
    if (typeof lemma === 'string' && lemma && typeof version === 'string') state.quarantined[lemma] = version;
  }
  state.serverContentVersion = typeof raw.serverContentVersion === 'string' ? raw.serverContentVersion : null;
  state.importedVersions = Array.from(new Set(
    Array.isArray(raw.importedVersions)
      ? raw.importedVersions.filter((version: unknown): version is string => typeof version === 'string' && !!version)
      : [],
  ));
  return state;
}

function readWordSync(): WordSyncState {
  if (!hasLocalStorage()) return memoryWordSync ?? emptyWordSync();
  try {
    const raw = window.localStorage.getItem(WORD_STATE_STORAGE_KEY);
    return raw ? normalizeWordSync(JSON.parse(raw)) : emptyWordSync();
  } catch {
    return memoryWordSync ?? emptyWordSync();
  }
}

function writeWordSync(state: WordSyncState): void {
  if (!hasLocalStorage()) { memoryWordSync = state; return; }
  try { window.localStorage.setItem(WORD_STATE_STORAGE_KEY, JSON.stringify(state)); }
  catch { memoryWordSync = state; }
}

function emptyProgress(contentVersion: string): VocabProgress {
  return {
    contentVersion,
    perWord: {},
    cursor: { deckId: 1, nextUnlearnedRank: 1 },
    resume: { lastWordId: null },
    drillMirror: {},
    confirmedSessionStats: null,
    serverMastered: {},
    serverDrillProgress: {},
    wordStateSyncStatus: 'not-measured',
    wordStateSyncError: null,
    starred: {},
    browseBookmark: null,
    updatedAt: new Date().toISOString(),
  };
}

/** Normalize a parsed-but-older-shape record onto the current `VocabProgress`
 * contract: `starred`/`browseBookmark` were added after the original v0
 * shape shipped, so any record read from storage (or held in the in-memory
 * fallback) that predates them must default rather than crash downstream
 * readers that assume the fields exist. */
function normalizeBookmark(raw: any): BrowseBookmark | null {
  if (!raw || typeof raw !== 'object') return null;
  const view = raw.view === 'starred' ? 'starred' : raw.view === 'all' ? 'all' : null;
  const page = raw.page;
  const wordId = raw.wordId ?? raw.word_id;
  if (!view || typeof page !== 'number' || !Number.isInteger(page) || page < 1 || typeof wordId !== 'string' || !wordId) {
    return null;
  }
  const bookmark: BrowseBookmark = { view, page, wordId };
  const savedAt = raw.savedAt ?? raw.saved_at;
  if (typeof savedAt === 'string' && savedAt) bookmark.savedAt = savedAt;
  return bookmark;
}

function emptySocial(): SocialState {
  return { starred: {}, bookmark: null, dirtyStars: {}, bookmarkDirty: false };
}

function normalizeSocial(raw: any): SocialState {
  const state = emptySocial();
  if (!raw || typeof raw !== 'object') return state;
  if (raw.starred && typeof raw.starred === 'object') {
    for (const [lemma, value] of Object.entries(raw.starred)) {
      if (value === true && lemma) state.starred[lemma] = true;
    }
  }
  state.bookmark = normalizeBookmark(raw.bookmark);
  if (raw.dirtyStars && typeof raw.dirtyStars === 'object') {
    for (const [lemma, value] of Object.entries(raw.dirtyStars)) {
      if (lemma && typeof value === 'boolean') state.dirtyStars[lemma] = value;
    }
  }
  state.bookmarkDirty = raw.bookmarkDirty === true;
  return state;
}

const memorySocialByScope = new Map<string, SocialState>();
const loggedUnresolvedSocialTokens = new Set<string>();

interface SocialScope {
  runtimeKey: string;
  storageKey: string | null;
  legacyStorageKeys: string[];
}

/** Persist only when account ownership is provable. An authenticated opaque
 * token still gets an isolated in-memory scope so its current-session write is
 * sent, but no dirty intent can survive and replay under another account. */
function currentSocialScope(logUnresolved: boolean = false): SocialScope | null {
  if (!isAuthenticated()) {
    const storageKey = `${SOCIAL_STORAGE_KEY_PREFIX}anon`;
    return {
      runtimeKey: storageKey,
      storageKey,
      legacyStorageKeys: [`${LEGACY_SCOPED_SOCIAL_STORAGE_KEY_PREFIX}anon`],
    };
  }
  const token = currentAccessToken();
  if (!token) return null;
  const identity = resolveAuthIdentity(token);
  if (identity?.storageScope) {
    const storageKey = `${SOCIAL_STORAGE_KEY_PREFIX}${identity.storageScope}`;
    const legacyAliases = [
      identity.legacySubject,
      identity.source === 'internal-user' ? identity.userId : null,
    ].filter((value): value is string => value !== null);
    const legacyStorageKeys = [...new Set(legacyAliases.map(
      (value) => `${LEGACY_SCOPED_SOCIAL_STORAGE_KEY_PREFIX}${value}`,
    ))];
    return { runtimeKey: storageKey, storageKey, legacyStorageKeys };
  }
  if (logUnresolved && !loggedUnresolvedSocialTokens.has(token)) {
    loggedUnresolvedSocialTokens.add(token);
    console.warn('[vocab] Authenticated social identity is unresolved; syncing without persistent replay state.');
  }
  return { runtimeKey: `session:${token}`, storageKey: null, legacyStorageKeys: [] };
}

function discardUnownedLegacySocialState(scope: SocialScope): void {
  if (!hasLocalStorage()) return;
  try {
    window.localStorage.removeItem(LEGACY_SOCIAL_STORAGE_KEY);
    // Bare legacy aliases contain no provider/account metadata. Even when the
    // current verified profile repeats an alias, it cannot prove which provider
    // created that record, so never import its replayable dirty intent.
    for (const key of scope.legacyStorageKeys) window.localStorage.removeItem(key);
  } catch { /* fail-safe reads remain empty */ }
}

function readSocial(): SocialState | null {
  const scope = currentSocialScope();
  if (!scope) return null;
  if (!scope.storageKey || !hasLocalStorage()) return memorySocialByScope.get(scope.runtimeKey) ?? null;
  try {
    discardUnownedLegacySocialState(scope);
    const raw = window.localStorage.getItem(scope.storageKey);
    return raw ? normalizeSocial(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeSocial(social: SocialState): void {
  const scope = currentSocialScope();
  if (!scope) return;
  if (!scope.storageKey || !hasLocalStorage()) {
    memorySocialByScope.set(scope.runtimeKey, social);
    return;
  }
  try {
    discardUnownedLegacySocialState(scope);
    window.localStorage.setItem(scope.storageKey, JSON.stringify(social));
  } catch {
    memorySocialByScope.set(scope.runtimeKey, social);
  }
}

function normalize(progress: VocabProgress, social: SocialState): VocabProgress {
  return {
    ...progress,
    starred: social.starred,
    browseBookmark: social.bookmark,
    confirmedSessionStats: progress.confirmedSessionStats ?? null,
  };
}

function hasLocalStorage(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const probeKey = '__pp_vocab_probe__';
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

// In-memory fallback used when localStorage is unavailable or over quota —
// degrades gracefully (cards fall back to first-visit on next real load) but
// keeps the store usable for the current session.
let memoryFallback: VocabProgress | null = null;

function readRaw(contentVersion: string): VocabProgress | null {
  if (!hasLocalStorage()) return memoryFallback;
  try {
    const raw = window.localStorage.getItem(storageKey(contentVersion));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Malformed / half-written JSON (or a shape that predates a field we now
    // require) is treated as missing, not as real progress — per the global
    // "half-written = missing" persistence rule.
    if (
      !parsed
      || typeof parsed !== 'object'
      || parsed.contentVersion !== contentVersion
      || typeof parsed.perWord !== 'object'
      || typeof parsed.cursor !== 'object'
    ) {
      return null;
    }
    return parsed as VocabProgress;
  } catch {
    return null;
  }
}

function writeRaw(progress: VocabProgress): void {
  const {
    starred: _starred, browseBookmark: _browseBookmark, serverMastered: _serverMastered,
    serverDrillProgress: _serverDrillProgress,
    wordStateSyncStatus: _wordStateSyncStatus, wordStateSyncError: _wordStateSyncError,
    ...versionedProgress
  } = progress;
  if (isAuthenticated()) versionedProgress.perWord = {};
  if (!hasLocalStorage()) {
    memoryFallback = progress;
    return;
  }
  try {
    window.localStorage.setItem(storageKey(progress.contentVersion), JSON.stringify(versionedProgress));
  } catch {
    // Quota exceeded or write blocked — degrade to in-memory for this session.
    memoryFallback = progress;
  }
}

function importVersionedWordStates(
  raw: VocabProgress,
  wordSync: WordSyncState,
  contentVersion: string,
): { raw: VocabProgress; wordSync: WordSyncState; importedAny: boolean } {
  if (!isAuthenticated() || wordSync.importedVersions.includes(contentVersion)) {
    return { raw, wordSync, importedAny: false };
  }
  const manualStates = { ...wordSync.manualStates };
  const dirtyWordStates = { ...wordSync.dirtyWordStates };
  let importedAny = false;
  for (const [lemma, state] of Object.entries(raw.perWord)) {
    if (state === 'unlearned') continue;
    if (Object.prototype.hasOwnProperty.call(manualStates, lemma)) continue;
    if (Object.prototype.hasOwnProperty.call(dirtyWordStates, lemma)) continue;
    manualStates[lemma] = state as ManualWordState;
    dirtyWordStates[lemma] = state as ManualWordState;
    importedAny = true;
  }
  const nextWordSync: WordSyncState = {
    ...wordSync,
    manualStates,
    dirtyWordStates,
    importedVersions: [...wordSync.importedVersions, contentVersion],
  };
  const nextRaw = { ...raw, perWord: {} };
  writeWordSync(nextWordSync);
  writeRaw(nextRaw);
  return { raw: nextRaw, wordSync: nextWordSync, importedAny };
}

export function getProgress(contentVersion: string = VOCAB_CONTENT_VERSION): VocabProgress {
  let raw = readRaw(contentVersion) ?? emptyProgress(contentVersion);
  let wordSync = readWordSync();
  const imported = importVersionedWordStates(raw, wordSync, contentVersion);
  raw = imported.raw;
  wordSync = imported.wordSync;
  // A successful GET may have completed before this versioned record was
  // read. A newly imported dirty intent must therefore reopen sync.
  if (imported.importedAny) wordStateSyncToken = null;
  let social = readSocial();
  // One-time migration from the old version-keyed shape. These entries are
  // dirty because the old client could not prove that the server had them.
  if (!isAuthenticated() && !social && (raw.starred || raw.browseBookmark)) {
    social = {
      starred: raw.starred && typeof raw.starred === 'object' ? raw.starred : {},
      bookmark: raw.browseBookmark ?? null,
      dirtyStars: Object.fromEntries(Object.keys(raw.starred ?? {}).map((lemma) => [lemma, true])),
      bookmarkDirty: raw.browseBookmark != null,
    };
    writeSocial(normalizeSocial(social));
  }
  social = social ?? emptySocial();
  maybeStartServerSync();
  return normalize({
    ...raw,
    perWord: isAuthenticated() ? wordSync.manualStates : raw.perWord,
    serverMastered: isAuthenticated() ? wordSync.serverMastered : {},
    serverDrillProgress: isAuthenticated() ? wordSync.serverDrillProgress : {},
    wordStateSyncStatus: isAuthenticated() ? wordSync.syncStatus : 'not-measured',
    wordStateSyncError: isAuthenticated() ? wordSync.lastError : null,
  }, social);
}

/** Has the learner made any progress at all (drives home-card first-visit vs
 * returning branch)? On any read failure this returns false — never render a
 * stale/zero cursor as real progress. */
export function hasProgress(contentVersion: string = VOCAB_CONTENT_VERSION): boolean {
  try {
    const p = readRaw(contentVersion);
    if (!p) return false;
    const confirmed = p.confirmedSessionStats;
    const confirmedAttempts = confirmed ? confirmed.learning + confirmed.familiar + confirmed.mastered : 0;
    return Object.keys(p.perWord).length > 0 || confirmedAttempts > 0;
  } catch {
    return false;
  }
}

// Lowest-rank word that is neither 'learned' nor 'skipped' — skipped words are
// jumped over; 'needs-review' does NOT block the cursor from advancing (design
// §8.8). Falls back to rank 1 if every word is learned/skipped.
export function recomputeCursor(progress: VocabProgress): VocabProgress['cursor'] {
  for (const w of VOCAB_INDEX.words) {
    const state = effectiveWordState(progress, w.lemma);
    if (state !== 'learned' && state !== 'skipped') {
      return { deckId: w.deck, nextUnlearnedRank: w.rank };
    }
  }
  const last = VOCAB_INDEX.words[VOCAB_INDEX.words.length - 1];
  return { deckId: last?.deck ?? 1, nextUnlearnedRank: last?.rank ?? 1 };
}

export function effectiveWordState(progress: VocabProgress, lemma: string): WordState {
  return progress.perWord[lemma] ?? (progress.serverMastered[lemma] ? 'learned' : 'unlearned');
}

export function setWordState(
  id: string,
  state: WordState,
  contentVersion: string = VOCAB_CONTENT_VERSION,
): VocabProgress {
  const current = getProgress(contentVersion);
  const next: VocabProgress = {
    ...current,
    perWord: { ...current.perWord, [id]: state },
    resume: { lastWordId: id },
    updatedAt: new Date().toISOString(),
  };
  next.cursor = recomputeCursor(next);
  if (isAuthenticated()) {
    wordStateMutationSequences.set(id, ++wordStateMutationSequence);
    const sync = readWordSync();
    const manualStates = { ...sync.manualStates };
    if (state === 'unlearned') delete manualStates[id];
    else manualStates[id] = state as ManualWordState;
    // An explicit user re-mark lifts a 404 quarantine for this word: the new
    // intent earns one fresh PUT (a repeat 404 re-quarantines it).
    const quarantined = { ...sync.quarantined };
    delete quarantined[id];
    const nextSync = {
      ...sync,
      manualStates,
      quarantined,
      dirtyWordStates: { ...sync.dirtyWordStates, [id]: state === 'unlearned' ? null : state as ManualWordState },
      syncStatus: sync.syncStatus === 'failed' ? 'failed' : sync.syncStatus,
    } as WordSyncState;
    writeWordSync(nextSync);
    next.perWord = manualStates;
  }
  writeRaw(next);
  notify(contentVersion, { type: 'word-state-intent', lemma: id, state });
  if (isAuthenticated()) {
    void trackPendingSync(pushWordState(
      id,
      state === 'unlearned' ? null : state as ManualWordState,
      wordStateMutationSequences.get(id),
    ));
  }
  return next;
}

export function markSkipped(id: string, contentVersion: string = VOCAB_CONTENT_VERSION): VocabProgress {
  return setWordState(id, 'skipped', contentVersion);
}

/** Retain a per-word drill mirror for backward-compatible resume metadata.
 * It is not confirmed server truth and therefore never mutates list-learning
 * state or any user-facing mastery count. */
export function recordDrillOutcome(
  id: string,
  mastery: MasteryState,
  contentVersion: string = VOCAB_CONTENT_VERSION,
): VocabProgress {
  const current = getProgress(contentVersion);
  const next: VocabProgress = {
    ...current,
    drillMirror: { ...current.drillMirror, [id]: mastery },
    updatedAt: new Date().toISOString(),
  };
  writeRaw(next);
  notify(contentVersion);
  return next;
}

function confirmedCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
}

/** Persist only the aggregate session stats returned by the server. */
export function recordConfirmedSessionStats(
  stats: ConfirmedSessionStats,
  contentVersion: string = VOCAB_CONTENT_VERSION,
): VocabProgress {
  const current = getProgress(contentVersion);
  const next: VocabProgress = {
    ...current,
    confirmedSessionStats: {
      unknown: confirmedCount(stats.unknown),
      learning: confirmedCount(stats.learning),
      familiar: confirmedCount(stats.familiar),
      mastered: confirmedCount(stats.mastered),
    },
    updatedAt: new Date().toISOString(),
  };
  writeRaw(next);
  notify(contentVersion);
  return next;
}

// --- Starred words (PM §3) --------------------------------------------

function apiSocialState(response: any): {
  starred: Record<string, true>;
  bookmark: BrowseBookmark | null;
  hasStarredField: boolean;
  hasBookmarkField: boolean;
} {
  const data = response?.data?.data ?? response?.data ?? {};
  const starred: Record<string, true> = {};
  const hasStarredField = Object.prototype.hasOwnProperty.call(data, 'vocab_starred');
  const hasBookmarkField = Object.prototype.hasOwnProperty.call(data, 'vocab_bookmark');
  if (data.vocab_starred && typeof data.vocab_starred === 'object') {
    for (const [lemma, value] of Object.entries(data.vocab_starred)) {
      if (value === true && lemma) starred[lemma] = true;
    }
  }
  return {
    starred,
    bookmark: normalizeBookmark(data.vocab_bookmark),
    hasStarredField,
    hasBookmarkField,
  };
}

function bookmarkPayload(bookmark: BrowseBookmark | null): any {
  if (!bookmark) return null;
  return { view: bookmark.view, page: bookmark.page, word_id: bookmark.wordId };
}

function currentAccessToken(): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage.getItem('access_token');
}

function isAuthenticated(): boolean {
  try {
    const checker = (userKgClient as any).hasRealAuthToken;
    if (typeof checker === 'function') return checker();
  } catch {
    // Some isolated consumers mock only analytics from this client module.
  }
  return isAuthLikeToken(currentAccessToken());
}

function apiClient(): any | null {
  try {
    return (userKgClient as any).nextClient ?? null;
  } catch {
    return null;
  }
}

let serverSyncKey: string | null = null;
let serverSyncPromise: Promise<void> | null = null;
let socialSyncGeneration = 0;
let wordStateSyncToken: string | null = null;
let wordStateSyncPromise: Promise<void> | null = null;
let wordStateSyncGeneration = 0;
const wordStateMutationSequences = new Map<string, number>();
let wordStateMutationSequence = 0;
let socialMutationSequence = 0;
const starMutationSequences = new Map<string, number>();
let bookmarkMutationSequence = 0;
const pendingSyncs = new Set<Promise<void>>();

function trackPendingSync(promise: Promise<void>): Promise<void> {
  pendingSyncs.add(promise);
  void promise.then(
    () => pendingSyncs.delete(promise),
    () => pendingSyncs.delete(promise),
  );
  return promise;
}

/** Wait until all currently-running store synchronization work, including
 * fire-and-forget mutation pushes, has reached a terminal state. The loop also
 * catches follow-up work started by a sync continuation while settling. */
export async function settlePendingSyncs(): Promise<void> {
  while (pendingSyncs.size > 0) {
    await Promise.allSettled(Array.from(pendingSyncs));
  }
}

interface WordStateRequestFence {
  token: string;
  generation: number;
}

interface SocialRequestFence {
  token: string;
  generation: number;
  scopeKey: string;
}

function socialRequestKey(token: string, scopeKey: string): string {
  return JSON.stringify([token, scopeKey]);
}

function isCurrentSocialRequest(fence: SocialRequestFence): boolean {
  return fence.generation === socialSyncGeneration
    && currentAccessToken() === fence.token
    && currentSocialScope()?.runtimeKey === fence.scopeKey
    && isAuthenticated();
}

function isCurrentWordStateRequest(fence: WordStateRequestFence): boolean {
  return fence.generation === wordStateSyncGeneration
    && currentAccessToken() === fence.token
    && isAuthenticated();
}

function recordStarMutation(lemma: string): void {
  starMutationSequences.set(lemma, ++socialMutationSequence);
}

function recordBookmarkMutation(): void {
  bookmarkMutationSequence = ++socialMutationSequence;
}

function applyStarIntents(
  starred: Record<string, true>,
  dirtyStars: Record<string, boolean>,
): Record<string, true> {
  const next = { ...starred };
  for (const [lemma, desired] of Object.entries(dirtyStars)) {
    if (desired) next[lemma] = true;
    else delete next[lemma];
  }
  return next;
}

function clearDirtyStarIfUnchanged(social: SocialState, lemma: string, starred: boolean): void {
  if (social.dirtyStars[lemma] !== starred) return;
  const isCurrentlyStarred = social.starred[lemma] === true;
  if (isCurrentlyStarred === starred) delete social.dirtyStars[lemma];
}

async function pushStar(lemma: string, starred: boolean): Promise<void> {
  const token = currentAccessToken();
  const scope = currentSocialScope(true);
  if (!isAuthenticated() || !token || !scope) return;
  const fence: SocialRequestFence = { token, generation: socialSyncGeneration, scopeKey: scope.runtimeKey };
  try {
    const client = apiClient();
    if (!client) return;
    const response = await client.put('/api/sat/user-profile', {
      star: { lemma, starred },
    });
    if (!isCurrentSocialRequest(fence)) return;
    const social = readSocial() ?? emptySocial();
    const returned = apiSocialState(response);
    if (returned.hasStarredField) {
      social.starred = applyStarIntents(returned.starred, social.dirtyStars);
      if (social.dirtyStars[lemma] === starred) clearDirtyStarIfUnchanged(social, lemma, starred);
    }
    writeSocial(social);
    notify(VOCAB_CONTENT_VERSION);
  } catch {
    // Resolvable accounts retain the dirty intent for replay; unresolved
    // sessions keep it only in token-isolated memory.
    if (isCurrentSocialRequest(fence)) serverSyncKey = null;
  }
}

async function pushBookmark(bookmark: BrowseBookmark | null): Promise<void> {
  const token = currentAccessToken();
  const scope = currentSocialScope(true);
  if (!isAuthenticated() || !token || !scope) return;
  const fence: SocialRequestFence = { token, generation: socialSyncGeneration, scopeKey: scope.runtimeKey };
  try {
    const client = apiClient();
    if (!client) return;
    const response = await client.put('/api/sat/user-profile', {
      bookmark: bookmarkPayload(bookmark),
    });
    if (!isCurrentSocialRequest(fence)) return;
    const social = readSocial() ?? emptySocial();
    const returned = apiSocialState(response);
    if (social.bookmarkDirty && JSON.stringify(social.bookmark) === JSON.stringify(bookmark)) {
      if (returned.hasBookmarkField) {
        social.bookmark = returned.bookmark;
        social.bookmarkDirty = false;
      }
    } else if (!social.bookmarkDirty && returned.hasBookmarkField) {
      social.bookmark = returned.bookmark;
    }
    writeSocial(social);
    notify(VOCAB_CONTENT_VERSION);
  } catch {
    // Resolvable accounts retain the dirty intent for replay; unresolved
    // sessions keep it only in token-isolated memory.
    if (isCurrentSocialRequest(fence)) serverSyncKey = null;
  }
}

/** Fetch durable social state and reconcile any local offline mutations. */
export function syncVocabSocialState(): Promise<void> {
  const token = currentAccessToken();
  const scope = currentSocialScope(true);
  if (!isAuthenticated() || !token || !scope) return Promise.resolve();
  const syncKey = socialRequestKey(token, scope.runtimeKey);
  if (serverSyncPromise) {
    if (serverSyncKey === syncKey) return serverSyncPromise;
    return serverSyncPromise.then(() => syncVocabSocialState());
  }
  if (serverSyncKey === syncKey) return Promise.resolve();
  const fence: SocialRequestFence = { token, generation: socialSyncGeneration, scopeKey: scope.runtimeKey };
  serverSyncKey = syncKey;
  let requestPromise!: Promise<void>;
  requestPromise = (async () => {
    let loaded = false;
    try {
      const local = readSocial() ?? emptySocial();
      const requestedStarMutations = new Map(starMutationSequences);
      const requestedBookmarkMutation = bookmarkMutationSequence;
      const client = apiClient();
      if (!client) return;
      const server = apiSocialState(await client.get('/api/sat/user-profile'));
      if (!isCurrentSocialRequest(fence)) return;
      if (!server.hasStarredField || !server.hasBookmarkField) throw new Error('Incomplete social profile response');
      loaded = true;
      const fresh = readSocial() ?? emptySocial();
      const merged: SocialState = {
        ...fresh,
        starred: server.hasStarredField ? { ...server.starred } : { ...fresh.starred },
        bookmark: server.hasBookmarkField ? server.bookmark : fresh.bookmark,
      };

      // The GET is only authoritative for state that was not changed while it
      // was in flight. Re-read after await so a concurrent toggle/PUT cannot
      // be replaced by the request-start snapshot. A successful PUT may have
      // already cleared its dirty flag, so the mutation sequence is also
      // needed to preserve that newer clean value against a stale GET.
      if (server.hasStarredField) {
        merged.starred = applyStarIntents(merged.starred, fresh.dirtyStars);
        for (const [lemma, sequence] of starMutationSequences) {
          if (sequence <= (requestedStarMutations.get(lemma) ?? 0)) continue;
          if (fresh.starred[lemma]) merged.starred[lemma] = true;
          else delete merged.starred[lemma];
        }
      }
      if (fresh.bookmarkDirty || bookmarkMutationSequence > requestedBookmarkMutation) {
        merged.bookmark = fresh.bookmark;
      }
      writeSocial(merged);
      notify(VOCAB_CONTENT_VERSION);

      const writes: Array<{ kind: 'star' | 'bookmark'; lemma?: string; starred?: boolean; bookmark?: BrowseBookmark | null; promise: Promise<void> }> = [];
      for (const [lemma, starred] of Object.entries(local.dirtyStars)) {
        writes.push({ kind: 'star', lemma, starred, promise: pushStar(lemma, starred) });
      }
      if (local.bookmarkDirty) {
        writes.push({ kind: 'bookmark', bookmark: local.bookmark, promise: pushBookmark(local.bookmark) });
      }
      await Promise.all(writes.map((write) => write.promise));
    } catch {
      // Keep the local state and dirty flags. The next authenticated load
      // retries instead of silently discarding offline changes.
    } finally {
      if (serverSyncPromise === requestPromise) {
        if (!isCurrentSocialRequest(fence)) {
          if (serverSyncKey === syncKey) serverSyncKey = null;
          serverSyncPromise = null;
        } else {
          const pending = readSocial();
          serverSyncKey = loaded
            && !!pending
            && Object.keys(pending.dirtyStars).length === 0
            && !pending.bookmarkDirty
            ? syncKey
            : null;
          serverSyncPromise = null;
        }
      }
    }
  })();
  serverSyncPromise = trackPendingSync(requestPromise);
  return serverSyncPromise;
}

function wordStateResponse(response: any): any {
  return response?.data?.data ?? response?.data ?? {};
}

function wordStateContentVersion(response: any): string | null {
  const version = response?.data?.meta?.content_version;
  return typeof version === 'string' && version ? version : null;
}

/** Record the collection version a 404 was observed under and note the reason
 * locally. The dirty intent is retained (the mark stays visible as unsynced)
 * but is excluded from replay until the active collection version changes. */
function quarantineWordState(lemma: string): void {
  const sync = readWordSync();
  sync.quarantined = { ...sync.quarantined, [lemma]: sync.serverContentVersion ?? '' };
  writeWordSync(sync);
  notify(VOCAB_CONTENT_VERSION);
}

function isQuarantined(sync: WordSyncState, lemma: string): boolean {
  return Object.prototype.hasOwnProperty.call(sync.quarantined, lemma)
    && sync.quarantined[lemma] === (sync.serverContentVersion ?? '');
}

function wordStateError(): string {
  return "Couldn't sync word progress. Retry.";
}

function setWordSyncStatus(status: WordSyncStatus, error: string | null): void {
  const sync = readWordSync();
  writeWordSync({ ...sync, syncStatus: status, lastError: error });
  notify(VOCAB_CONTENT_VERSION);
}

function statsFromWordState(value: any): ConfirmedSessionStats | null {
  const stats = value?.stats;
  if (!stats || ![stats.unknown, stats.learning, stats.familiar, stats.mastered].every((n: any) => Number.isFinite(n))) return null;
  return { unknown: stats.unknown, learning: stats.learning, familiar: stats.familiar, mastered: stats.mastered };
}

async function pushWordState(
  lemma: string,
  state: ManualWordState | null,
  sequence = wordStateMutationSequences.get(lemma) ?? 0,
  requestFence?: WordStateRequestFence,
): Promise<void> {
  const token = currentAccessToken();
  if (!isAuthenticated() || !token) return;
  const fence = requestFence ?? { token, generation: wordStateSyncGeneration };
  if (!isCurrentWordStateRequest(fence)) return;
  try {
    const client = apiClient();
    if (!client) throw new Error('word-state client unavailable');
    const response = await client.put('/api/sat/vocab/word-states', { lemma, state });
    if (!isCurrentWordStateRequest(fence)) return;
    const returned = wordStateResponse(response);
    const sync = readWordSync();
    const version = wordStateContentVersion(response);
    if (version) sync.serverContentVersion = version;
    delete sync.quarantined[lemma];
    const currentSequence = wordStateMutationSequences.get(lemma) ?? 0;
    const desired = sync.dirtyWordStates[lemma];
    if (desired === state && currentSequence === sequence) {
      delete sync.dirtyWordStates[lemma];
      if (state === null) delete sync.manualStates[lemma];
    }
    const confirmed = statsFromWordState(returned);
    if (confirmed) {
      const p = readRaw(VOCAB_CONTENT_VERSION) ?? emptyProgress(VOCAB_CONTENT_VERSION);
      writeRaw({ ...p, confirmedSessionStats: confirmed, updatedAt: new Date().toISOString() });
    }
    sync.syncStatus = 'measured';
    sync.lastError = null;
    writeWordSync(sync);
    notify(VOCAB_CONTENT_VERSION, {
      type: 'word-state-confirmed', lemma, state: state ?? 'unlearned',
    });
  } catch (error: any) {
    if (!isCurrentWordStateRequest(fence)) return;
    if (error?.response?.status === 404) {
      // The word is not in the active collection (dropped lemma). Keep the
      // local mark as unsynced but stop replaying it — a retry loop cannot
      // succeed until the active collection version changes (design §7).
      quarantineWordState(lemma);
      notify(VOCAB_CONTENT_VERSION, {
        type: 'word-state-failed', lemma, state: state ?? 'unlearned',
      });
      return;
    }
    setWordSyncStatus('failed', wordStateError());
    notify(VOCAB_CONTENT_VERSION, {
      type: 'word-state-failed', lemma, state: state ?? 'unlearned',
    });
    wordStateSyncToken = null;
  }
}

async function replayWordStateIntents(
  intents: Array<[string, ManualWordState | null]>,
  fence: WordStateRequestFence,
): Promise<void> {
  if (!isCurrentWordStateRequest(fence)) return;
  const sync = readWordSync();
  const eligible = intents.filter(([lemma]) => !isQuarantined(sync, lemma));
  let cursor = 0;
  const worker = async () => {
    while (cursor < eligible.length) {
      if (!isCurrentWordStateRequest(fence)) return;
      const index = cursor++;
      const [lemma, state] = eligible[index];
      await pushWordState(lemma, state, wordStateMutationSequences.get(lemma) ?? 0, fence);
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, eligible.length) }, () => worker()));
}

export function syncVocabWordStates(): Promise<void> {
  const token = currentAccessToken();
  if (!isAuthenticated() || !token) return Promise.resolve();
  // Import before any authenticated write can clear the versioned anonymous
  // map. This also covers auth boot paths that start sync before rendering a
  // consumer that calls getProgress().
  importVersionedWordStates(
    readRaw(VOCAB_CONTENT_VERSION) ?? emptyProgress(VOCAB_CONTENT_VERSION),
    readWordSync(),
    VOCAB_CONTENT_VERSION,
  );
  if (wordStateSyncPromise && wordStateSyncToken === token) return wordStateSyncPromise;
  if (wordStateSyncToken === token) return Promise.resolve();
  const fence: WordStateRequestFence = { token, generation: wordStateSyncGeneration };
  wordStateSyncToken = token;
  let requestPromise!: Promise<void>;
  requestPromise = (async () => {
    let measured = false;
    try {
      const client = apiClient();
      if (!client) throw new Error('word-state client unavailable');
      const local = readWordSync();
      const requestSequences = new Map(wordStateMutationSequences);
      const rawResponse = await client.get('/api/sat/vocab/word-states');
      if (!isCurrentWordStateRequest(fence)) return;
      const response = wordStateResponse(rawResponse);
      const serverVersion = wordStateContentVersion(rawResponse);
      const fresh = readWordSync();
      const serverManual: Record<string, ManualWordState> = {};
      for (const [lemma, value] of Object.entries(response.manual_states ?? {})) {
        const state = (value as any)?.state;
        if (['learning', 'learned', 'needs-review', 'skipped'].includes(state)) serverManual[lemma] = state;
      }
      const merged: WordSyncState = {
        ...fresh,
        manualStates: serverManual,
        serverMastered: {},
        serverDrillProgress: {},
        syncStatus: 'measured',
        lastError: null,
      };
      // A new active collection version lifts 404 quarantines: dropped words
      // may exist again, so their retained local marks become replayable.
      if (serverVersion && serverVersion !== fresh.serverContentVersion) merged.quarantined = {};
      if (serverVersion) merged.serverContentVersion = serverVersion;
      for (const lemma of Object.keys(fresh.dirtyWordStates)) {
        const desired = fresh.dirtyWordStates[lemma];
        if (desired === null) delete merged.manualStates[lemma];
        else merged.manualStates[lemma] = desired;
      }
      for (const [lemma, sequence] of wordStateMutationSequences) {
        if (sequence > (requestSequences.get(lemma) ?? 0)) {
          const desired = fresh.dirtyWordStates[lemma];
          if (desired === null) delete merged.manualStates[lemma];
          else if (desired) merged.manualStates[lemma] = desired;
        }
      }
      for (const lemma of response.drill_mastered ?? []) if (typeof lemma === 'string') merged.serverMastered[lemma] = true;
      for (const [lemma, value] of Object.entries(response.drill_progress ?? {})) {
        if ((value === 'familiar' || value === 'learning') && typeof lemma === 'string' && lemma) {
          merged.serverDrillProgress[lemma] = value;
        }
      }
      if (!isCurrentWordStateRequest(fence)) return;
      writeWordSync(merged);
      const p = readRaw(VOCAB_CONTENT_VERSION) ?? emptyProgress(VOCAB_CONTENT_VERSION);
      const confirmed = statsFromWordState(response);
      const mergedProgress: VocabProgress = {
        ...p,
        perWord: merged.manualStates,
        serverMastered: merged.serverMastered,
        serverDrillProgress: merged.serverDrillProgress,
        confirmedSessionStats: confirmed,
        updatedAt: new Date().toISOString(),
      };
      mergedProgress.cursor = recomputeCursor(mergedProgress);
      writeRaw(mergedProgress);
      notify(VOCAB_CONTENT_VERSION);
      measured = true;
      await replayWordStateIntents(Object.entries(local.dirtyWordStates), fence);
    } catch {
      if (!isCurrentWordStateRequest(fence)) return;
      setWordSyncStatus('failed', wordStateError());
      wordStateSyncToken = null;
    } finally {
      if (wordStateSyncPromise === requestPromise) {
        if (!isCurrentWordStateRequest(fence)) {
          if (wordStateSyncToken === token) wordStateSyncToken = null;
          wordStateSyncPromise = null;
        } else {
          const pending = readWordSync();
          // Quarantined intents are intentionally parked — they must not hold the
          // sync token open (that would re-GET on every read in a futile loop).
          const replayable = Object.keys(pending.dirtyWordStates)
            .filter((lemma) => !isQuarantined(pending, lemma));
          if (!replayable.length && measured) wordStateSyncToken = token;
          wordStateSyncPromise = null;
        }
      }
    }
  })();
  wordStateSyncPromise = trackPendingSync(requestPromise);
  return requestPromise;
}

export function invalidateVocabWordStateSync(): void { wordStateSyncToken = null; }

export function hasPendingWordStateSync(): boolean {
  // Quarantined intents are parked, not in-flight: counting them here would
  // pin a perpetual "Saving…" affordance for a word the server has dropped
  // (fail-honest: parked ≠ pending).
  const sync = readWordSync();
  return Object.keys(sync.dirtyWordStates).some((lemma) => !isQuarantined(sync, lemma));
}

export function quarantinedWordCount(): number {
  const sync = readWordSync();
  return Object.keys(sync.dirtyWordStates)
    .filter((lemma) => isQuarantined(sync, lemma)).length;
}

export function purgeVocabWordStateLocalState(): void {
  // Sign-out/test reset invalidates every response continuation, including
  // social profile writes, so an old account/request cannot write or notify
  // after the store lifecycle has been reset.
  // Account-scoped social records intentionally survive logout: they are that
  // account's device-local pending state and are read again only after the same
  // collision-proof verified account scope signs in.
  socialSyncGeneration += 1;
  wordStateSyncGeneration += 1;
  if (hasLocalStorage()) { try { window.localStorage.removeItem(WORD_STATE_STORAGE_KEY); } catch { /* memory reset below */ } }
  memoryWordSync = null;
  memorySocialByScope.clear();
  loggedUnresolvedSocialTokens.clear();
  serverSyncKey = null;
  serverSyncPromise = null;
  wordStateSyncToken = null;
  wordStateSyncPromise = null;
  wordStateMutationSequences.clear();
  starMutationSequences.clear();
  socialMutationSequence = 0;
  bookmarkMutationSequence = 0;
}

function maybeStartServerSync(): void {
  if (isAuthenticated()) {
    void syncVocabSocialState();
    void syncVocabWordStates();
  }
}

/** Curation only — never touches perWord/drillMirror/cursor, so no
 * `recomputeCursor` call here (unlike setWordState/recordDrillOutcome). */
export function toggleStar(id: string, contentVersion: string = VOCAB_CONTENT_VERSION): VocabProgress {
  const current = getProgress(contentVersion);
  const nextStarred = { ...current.starred };
  const starred = !nextStarred[id];
  if (!starred) {
    delete nextStarred[id];
  } else {
    nextStarred[id] = true;
  }
  const next: VocabProgress = { ...current, starred: nextStarred, updatedAt: new Date().toISOString() };
  const social = readSocial() ?? emptySocial();
  social.starred = nextStarred;
  social.dirtyStars[id] = starred;
  recordStarMutation(id);
  writeSocial(social);
  writeRaw(next);
  notify(contentVersion);
  void trackPendingSync(pushStar(id, starred));
  return next;
}

export function isStarred(id: string, progress: VocabProgress): boolean {
  return Boolean(progress.starred[id]);
}

export function starredCount(progress: VocabProgress): number {
  return Object.keys(progress.starred).length;
}

// --- Bookmark / return position (PM §4) --------------------------------

export function saveBookmark(
  bookmark: BrowseBookmark,
  contentVersion: string = VOCAB_CONTENT_VERSION,
): VocabProgress {
  const current = getProgress(contentVersion);
  const next: VocabProgress = { ...current, browseBookmark: bookmark, updatedAt: new Date().toISOString() };
  const social = readSocial() ?? emptySocial();
  social.bookmark = bookmark;
  social.bookmarkDirty = true;
  recordBookmarkMutation();
  writeSocial(social);
  writeRaw(next);
  notify(contentVersion);
  void trackPendingSync(pushBookmark(bookmark));
  return next;
}

export function removeBookmark(contentVersion: string = VOCAB_CONTENT_VERSION): VocabProgress {
  const current = getProgress(contentVersion);
  const next: VocabProgress = { ...current, browseBookmark: null, updatedAt: new Date().toISOString() };
  const social = readSocial() ?? emptySocial();
  social.bookmark = null;
  social.bookmarkDirty = true;
  recordBookmarkMutation();
  writeSocial(social);
  writeRaw(next);
  notify(contentVersion);
  void trackPendingSync(pushBookmark(null));
  return next;
}

// --- % done union (§8.7) ---------------------------------------------------

export interface ProgressUnion {
  learnedCount: number;
  total: number;
  percent: number;
}

/** Browse/list learning progress only. Drill mastery is a separate server-
 * confirmed measurement and is intentionally excluded. */
export function progressUnion(progress: VocabProgress): ProgressUnion {
  const total = VOCAB_INDEX.n_words;
  let learnedCount = 0;
  for (const w of VOCAB_INDEX.words) {
    if (effectiveWordState(progress, w.lemma) === 'learned') learnedCount += 1;
  }
  return { learnedCount, total, percent: total > 0 ? Math.round((learnedCount / total) * 100) : 0 };
}

export interface MasteryCounts {
  mastered: number;
  familiar: number;
  learning: number;
  unseen: number;
  total: number;
}

/** Confirmed server mastery only. With no snapshot yet, report the honest
 * lower bound (zero measured mastery) rather than promote local list state. */
export function masteryCounts(progress: VocabProgress): MasteryCounts | null {
  const confirmed = progress.confirmedSessionStats;
  if (!confirmed) return null;
  const mastered = confirmedCount(confirmed.mastered);
  const familiar = confirmedCount(confirmed.familiar);
  const learning = confirmedCount(confirmed.learning);
  const unseen = confirmedCount(confirmed.unknown);
  return { mastered, familiar, learning, unseen, total: mastered + familiar + learning + unseen };
}

// --- reactive subscribe ------------------------------------------------

type Listener = (event: VocabProgressEvent) => void;
const listeners = new Set<Listener>();

function notify(contentVersion: string, event: VocabProgressEvent = { type: 'changed' }): void {
  if (contentVersion !== VOCAB_CONTENT_VERSION) return;
  listeners.forEach((l) => l(event));
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel('pp.vocab.word-state');
    channel.postMessage(event);
    channel.close();
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Re-export for callers that want the derived deck of the cursor rank without
// recomputing it themselves.
export { deckIdOf };
